import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CityService from '../../../services/city.service';
import ActivityTypeService from '../../../services/activityType.service';
import EventService from '../../../services/event.service';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px',
      minWidth: '500px'
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between', 
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '24px'
  },
  actions: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  },
  submitButton: {
    backgroundColor: 'primary.main',
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#333'
    }
  },
  cancelButton: {
    color: '#666',
    borderRadius: '8px',
    textTransform: 'none'
  }
};

function Form({ open, onClose, initialData = null, onSubmit, onCancel }) {
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    date_start: '',
    date_end: '',
    city_id: '',
    activity_type_id: '',
    event_id: ''
  });

  const [cities, setCities] = React.useState([]);
  const [activityTypes, setActivityTypes] = React.useState([]);
  const [events, setEvents] = React.useState([]);

  React.useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [citiesData, typesData, eventsData] = await Promise.all([
          CityService.list(),
          ActivityTypeService.list(),
          EventService.list()
        ]);
        setCities(citiesData);
        setActivityTypes(typesData);
        setEvents(eventsData);
      } catch (error) {
        console.error('Erreur lors du chargement des données de référence:', error);
      }
    };
    loadReferenceData();
  }, []);

  React.useEffect(() => {
    if (initialData) {
      const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${date.toISOString().split('.')[0]}`;
      };

      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        date_start: formatDateTime(initialData.date_start),
        date_end: formatDateTime(initialData.date_end),
        city_id: initialData.city?.id || '',
        activity_type_id: initialData.activity_type?.id || '',
        event_id: initialData.event?.id || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        date_start: '',
        date_end: '',
        city_id: '',
        activity_type_id: '',
        event_id: ''
      });
    }
  }, [initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const submittedData = {
      ...formData,
      id: initialData?.id
    };
    onSubmit(submittedData);
  };

  const handleCancel = () => {
    onCancel();
    setFormData({
      name: '',
      description: '',
      date_start: '',
      date_end: '',
      city_id: '',
      activity_type_id: '',
      event_id: ''
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <span>{initialData ? 'Modifier l\'activité' : 'Nouvelle activité'}</span>
        <IconButton
          onClick={handleCancel}
          size="small"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={styles.form}>
          <TextField
            fullWidth
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="Description"
            name="description"d
            value={formData.description}
            onChange={handleChange}
            required
            multiline
            rows={4}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Date et heure de début"
              name="date_start"
              type="datetime-local"
              value={formData.date_start}
              onChange={handleChange}
              required
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Date et heure de fin"
              name="date_end"
              type="datetime-local"
              value={formData.date_end}
              onChange={handleChange}
              required
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <TextField
            select
            fullWidth
            label="Ville"
            name="city_id"
            value={formData.city_id}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          >
            {cities.map((city) => (
              <MenuItem key={city.id} value={city.id}>
                {city.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Type d'activité"
            name="activity_type_id"
            value={formData.activity_type_id}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          >
            {activityTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Événement"
            name="event_id"
            value={formData.event_id}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          >
            {events.map((event) => (
              <MenuItem key={event.id} value={event.id}>
                {event.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button
            onClick={handleCancel}
            sx={styles.cancelButton}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={styles.submitButton}
          >
            {initialData ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    date_start: PropTypes.string,
    date_end: PropTypes.string,
    city: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    activity_type: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string  
    }),
    event: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })
  })
};

export default Form;